/**
 * View component for /admin/authoring/content-modules
 *
 * Generic contentModule list view. Defaults to 'all' with:
 * this.props.dispatch(contentModuleActions.fetchListIfNeeded());
 *
 * NOTE: See /product/views/ProductList.js.jsx for more examples
 */

// import primary libraries
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link, withRouter } from 'react-router-dom';

// import actions
import * as contentModuleActions from '../contentModuleActions';

// import global components
import AuthoringLayout from '../../admin/components/AuthoringLayout.js.jsx';
import Base from "../../../global/components/BaseComponent.js.jsx";
import Breadcrumbs from "../../../global/components/routing/Breadcrumbs.js.jsx";
import Pagination from "../../../global/components/helpers/Pagination.js.jsx";
import { SearchInput } from '../../../global/components/forms';

// import contentModule components
import AdminContentModuleListItem from '../components/AdminContentModuleListItem.js.jsx';

// import utilities
import filterUtils from '../../../global/utils/filterUtils';

// import contentModule css modules
import contentModuleStyles from '../contentModuleModuleStyles.css';

class AdminContentModuleList extends Base {
  constructor(props) {
    super(props);
    this.state = {
      perPage: 50
      , queryText: this.props.query || ''
    };
    this._bind(
      '_handleQuery'
      , '_handleSetPagination'
      , '_handleSort'
      , '_resetPagination'
      , '_setPerPage'
    );
  }

  componentDidMount() {
    const { dispatch } = this.props;
    // fetch a list of your choice
    dispatch(contentModuleActions.fetchListIfNeeded('all')); // defaults to 'all'
    dispatch(contentModuleActions.setFilter({query: '', sortBy: 'title'}));
    dispatch(contentModuleActions.setPagination({page: 1, per: 50}));
  }

  _handleSort(sortBy) {
    const { dispatch, match, contentModuleList } = this.props;
    let newFilter = contentModuleList.filter;
    if(contentModuleList.filter.sortBy && contentModuleList.filter.sortBy.indexOf("-") < 0) {
      sortBy = "-" + sortBy;
    } else {
      sortBy = sortBy.substring(0)
    }
    newFilter.sortBy = sortBy;
    dispatch(contentModuleActions.setFilter(newFilter));
  }

  _resetPagination() {
    const { dispatch, match } = this.props;
    dispatch(contentModuleActions.setPagination({
      page: 1,
      per: parseInt(this.state.perPage)
    }));
  }

  _setPerPage(per) {
    const { dispatch, match, contentModuleList } = this.props;
    let newPagination = contentModuleList.pagination;
    newPagination.per = parseInt(per);
    newPagination.page = 1;
    dispatch(contentModuleActions.setPagination(newPagination));
    this.setState({perPage: per});
  }

  _handleSetPagination(newPagination) {
    const { dispatch, match } = this.props;
    dispatch(contentModuleActions.setPagination(newPagination));
  }

  _handleQuery(e) {
    const { dispatch, match, contentModuleList } = this.props;
    // always defaulting the page to page 1 so we can see our results
    let pagination = {};
    pagination.page = 1;
    pagination.per = contentModuleList.pagination.per;
    this._handleSetPagination(pagination);
    // continue query logic
    dispatch(contentModuleActions.setFilter({
      query: e.target.value.toLowerCase()
      , sortBy: contentModuleList.filter.sortBy
    }));
  }

  render() {
    const {
      location
      , paginatedList
      , sortedList
      , contentModuleList
      , contentModuleStore
    } = this.props;

    /**
     * Retrieve the list information and the list items for the component here.
     *
     * NOTE: if the list is deeply nested and/or filtered, you'll want to handle
     * these steps within the mapStoreToProps method prior to delivering the
     * props to the component.  Othwerwise, the render() action gets convoluted
     * and potentially severely bogged down.
     */

    /**
     * NOTE: isEmpty is is usefull when the component references more than one
     * resource list.
     */
    const isEmpty = !contentModuleList || !contentModuleList.items || contentModuleList.didInvalidate;
    const sortedEmpty = sortedList.length === 0;

    return (
      <AuthoringLayout>
        <div className="flex">
          <section className="section">
            <div className="yt-container">
              <h1> Content Modules
                <Link className="yt-btn create u-pullRight" to={'/admin/authoring/content-modules/new'}> 
                  <img className="-icon" src={'/img/create-white.png'}></img>
                </Link>
              </h1>
              <hr/>
              { isEmpty ?
                (
                  contentModuleList && contentModuleList.isFetching ?
                  <div className="u-centerText">
                    <div className="loading"></div>
                  </div>
                  :
                  <h2>Empty.</h2>
                )
                :
                <div style={{ opacity: contentModuleList.isFetching ? 0.5 : 1 }}>
                  <div className="yt-toolbar">
                    <div className="yt-tools right">
                      <div className="search">
                        <SearchInput
                          name="query"
                          value={contentModuleList.filter.query}
                          change={this._handleQuery}
                          placeholder="Search..."
                          required={false}
                          />
                      </div>
                    </div>
                  </div>
                  <div className="content-container">
                    <div className="table-wrapper">
                      <table className="yt-table striped">
                        <caption>
                          { contentModuleList.filter && contentModuleList.filter.query && contentModuleList.filter.query.length > 0 ?
                            <span>Filtered Content Modules &mdash; {sortedList.length} of {Object.keys(contentModuleList.items).length}</span>
                            :
                            <span>All Content Modules &mdash; {Object.keys(contentModuleList.items).length}</span>
                          }
                          <div className="per-page-select u-pullRight">
                            <label>Show per page: </label>
                            <select
                              name="numPerPage"
                              onChange={(e) => this._setPerPage(e.target.value)}
                              value={this.state.perPage}
                              >
                              <option value={25}> 25 </option>
                              <option value={50}> 50 </option>
                              <option value={100}> 100 </option>
                            </select>
                          </div>
                        </caption>
                        <thead>
                          <tr>
                            <th className="_30 sortable" onClick={()=> this._handleSort('title')}>
                              Title
                              {contentModuleList.filter.sortBy === 'title' ?
                                <i className="ion ion-ios-arrow-down" />
                                : contentModuleList.filter.sortBy === '-title' ?
                                <i className="ion ion-ios-arrow-up" />
                                : null
                              }
                            </th>
                            <th className="_50">Summary </th>
                            <th className="_20 sortable" onClick={()=> this._handleSort('updated')}>
                              Last Modified
                              {contentModuleList.filter.sortBy === 'updated' ?
                                <i className="ion ion-ios-arrow-down" />
                                : contentModuleList.filter.sortBy === '-updated' ?
                                <i className="ion ion-ios-arrow-up" />
                                : null
                              }
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {
                            paginatedList.map((contentModule, i) =>
                            <AdminContentModuleListItem
                              key={contentModule._id}
                              contentModule={contentModule}
                              />
                          )
                        }
                      </tbody>
                    </table>
                    { sortedEmpty ?
                      null
                      :
                      <Pagination
                        pagination={contentModuleList.pagination}
                        setPagination={this._handleSetPagination}
                        totalPages={ Math.floor(sortedList.length / contentModuleList.pagination.per) + 1}
                        />
                    }
                  </div>
                </div>
              </div>
            }
          </div>
        </section>
      </div>
      </AuthoringLayout>
    )
  }
}

AdminContentModuleList.propTypes = {
  dispatch: PropTypes.func.isRequired
}

const mapStoreToProps = (store) => {
  /**
  * NOTE: Yote refer's to the global Redux 'state' as 'store' to keep it mentally
  * differentiated from the React component's internal state
  */
  const contentModuleStore = store.contentModule;
  const contentModuleList = contentModuleStore.lists ? contentModuleStore.lists.all : null;
  let sortedList = [];
  let paginatedList = [];
  let filteredByQuery = [];
  if (contentModuleList) {

    const pagination = contentModuleList.pagination;
    const filter = contentModuleList.filter;
    const query = filter ? filter.query : '';
    const sortBy = filter ? filter.sortBy : 'email';

    // FILTER BY QUERY
    let queryTestString = ("" + query).toLowerCase().trim();
    // queryTestString = queryTestString.replace(/[^a-zA-Z0-9]/g,''); // replace all non-characters and numbers
    filteredByQuery = contentModuleList && contentModuleList.items ? contentModuleList.items.filter((contentModuleId) => {
      return filterUtils.filterContentModule(queryTestString, contentModuleStore.byId[contentModuleId]);
    }) : []

    // SORT THE LIST
    sortedList = filteredByQuery.map((item) => {
      let newItem = contentModuleStore.byId[item];
      return newItem;
    });
    switch(sortBy) {
      case 'title':
        sortedList = _.orderBy(sortedList, [item => item.title.toLowerCase()], ['asc']);
        break;
      case '-title':
        sortedList = _.orderBy(sortedList, [item => item.title.toLowerCase()], ['desc']);
        break;
      case 'updated':
        sortedList = _.orderBy(sortedList, [item => item.updated], ['asc']);
        break;
      case '-updated':
        sortedList = _.orderBy(sortedList, [item => item.updated], ['desc']);
        break;
      default:
        sortedList = _.orderBy(sortedList, [item => item.title.toLowerCase()], ['asc']);
    }

    // apply pagination
    let start = pagination ? (pagination.page - 1) * pagination.per : 1;
    let end = pagination ? start + pagination.per : 0;
    paginatedList = _.slice(sortedList, start, end);

  }
  return {
    paginatedList
    , sortedList
    , contentModuleList
    , contentModuleStore
  }
}

export default withRouter(
  connect(
    mapStoreToProps
  )(AdminContentModuleList)
);



// WEBPACK FOOTER //
// modules/contentModule/views/AdminContentModuleList.js.jsx